import Controller, { OnAfterFetch, OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import {DevicesReplaceActionAliases, FragmentNames} from '../../../codegen';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import {Inject} from '@odinjs/odin';
import { DynamicFieldsAPI, FieldAPI, FieldsetAPI, FormAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import {Endpoint} from '../../../constants/endpoints';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { CaliberType, SideType, Topography, TopographyType } from './devices-replace-action-types'
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../app/authentication';
import { ActionType, DevicesViewerType, ReasonType } from '../commons/register-device';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableSubmitEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { DevicesDynamicForm } from '../commons/devices-dynamic-form';

enum DeviceType {
  DEVICE_TYPE = 'S',
}

@Controller({
  component: DevicesReplaceActionAliases.LAYOUT_DEVICES_REPLACE_ACTION,
  fragment: FragmentNames.DEVICES_REPLACE_ACTION
})
class DevicesReplaceActionController extends BaseController {

  @Inject(DevicesReplaceActionAliases.FORM_DEVICES_REPLACE_ACTION_VIEWER)
  formDeviceReplaceAPI!: FormAPI;

  @Inject(DevicesReplaceActionAliases.FIELD_TOPOGRAPHY)
  topographyFieldAPI!: FieldAPI;

  @Inject(DevicesReplaceActionAliases.FIELD_SIDE)
  sideFieldAPI!: FieldAPI;

  @Inject(DevicesReplaceActionAliases.FIELDSET_DYNAMIC_FIELDS)
  fieldsetDynamicAPI!: FieldsetAPI;

  @Inject(DevicesReplaceActionAliases.DYNAMIC_FIELDS)
  dynamicFieldAPI!: DynamicFieldsAPI;

  @Inject(DevicesReplaceActionAliases.FIELD_ACTION_REMOVE)
  fieldActionRemoveAPI!: FieldAPI;

  @Inject(DevicesReplaceActionAliases.FIELD_REASON_WITHDWAWN)
  fieldReasonWithdrawnAPI!: FieldAPI;

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private submitReplace(body: Record<string, Value>): void {
    HttpService.post(Endpoint.DEVICES_REGISTER, undefined, {body})
      .then(() => {
        this.showToast('text.replace-devices', ToastLevel.SUCCESS)
        applicationContext.popNavigationItem();
        this.historyService.back();
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
  }

  @OnMount(DevicesReplaceActionAliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields') as Metadata;
    this.dynamicFieldAPI.setMetadata(dynamicFields);
  }

  private handleDynamicFields(): void {
    const {deviceSequence} = this.historyService.getCurrentRouteParams();
    const deviceType = DeviceType.DEVICE_TYPE;

    DevicesDynamicForm.handleDynamicFields(this.fieldsetDynamicAPI, deviceSequence, deviceType, this.context);
  }

  @OnBeforeSubmit({ target: DevicesReplaceActionAliases.FORM_DEVICES_REPLACE_ACTION_VIEWER })
  handleBeforeSubmitForm(event: AbortableSubmitEvent): void {
    event.abort();
    const formInfos = DevicesDynamicForm.createFormPattern(event);
    const formData = this.formDeviceReplaceAPI.getRecord();
    const {patientEncounterDeviceId, encounterId} = this.historyService.getCurrentRouteParams();
    const devSeq = this.context.get('deviceSequence');
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formatDateTime = moment(formData.date as string).utc().format(ISO_FULL_DATE_FORMAT);
    const observation = formData.observation as string;
    const actionInstall = formData.actionRemove as ActionType;
    const topography = formData.topography as Topography;
    const side = formData.side as SideType;
    const caliber = formData.caliber as CaliberType;
    const reason = formData.reasonWithdwawn as ReasonType;
    const body = {
      actionInstallActionSequence: actionInstall?.internalProcedureSequence,
      caliberSequence: caliber?.caliberSequence,
      deviceAttributes: formInfos,
      deviceSequence: devSeq,
      encounterId: encounterId as number,
      eventDescription: observation,
      optionStatus: DeviceType.DEVICE_TYPE,
      professionalCode: user.physicalPersonId,
      registerDate: formatDateTime,
      registerSequence: patientEncounterDeviceId as number,
      releasedStatus:'S',
      removedReasonSequence: reason?.reasonRemovedSequence,
      removeStatusDischarge: 'N',
      sideStatus: side?.sideDeviceCode,
      statusDeviceOption: 'P',
      successStatus: 'S',
      topographySequence: topography?.topographySequence,
    };
    this.submitReplace(body);
  }

  @OnChange({ target: DevicesReplaceActionAliases.FIELD_TOPOGRAPHY })
  onTopographyChange(event: ChangeEvent<FieldAPI>): void {
    const value = event.value as unknown as Topography;
    if (event.value) {
      const topographySequence = value.topographySequence;
      HttpService.get(Endpoint.GET_IS_SHOW_SIDE, {
        topographySequence
      }).then((response) => {
        const res = response.body as TopographyType;
        if (res.showSide === 'S') {
          this.sideFieldAPI.setVisible(true);
        } else {
          this.sideFieldAPI.setValue(null);
          this.sideFieldAPI.setVisible(false);
        }
      });
    } else {
      this.sideFieldAPI.setValue(null);
      this.sideFieldAPI.setVisible(false);
    }
  }

  @OnMount({ target: DevicesReplaceActionAliases.FORM_DEVICES_REPLACE_ACTION_VIEWER })
  onMount(): void {
    const {patientEncounterDeviceId, patientId, encounterId} = this.historyService.getCurrentRouteParams();
    const today = moment();
    const date = today.clone();
    this.formDeviceReplaceAPI.setLoading(true);
    HttpService.get(Endpoint.GET_DEVICES_VIEWER, {
      encounterId,
      patientEncounterDeviceId,
      patientId,
    }).then((response) => {
      const res = response.body as DevicesViewerType;
      const deviceDescription = res.deviceDescription;
      const deviceSequence = res.deviceSequence;
      this.context.set({
        deviceSequence: deviceSequence,
        encounterId: encounterId,
        patientId: patientId,
      });
      this.formDeviceReplaceAPI.setData({date, deviceDescription});
      this.handleDynamicFields();
    }).finally(() => this.formDeviceReplaceAPI.setLoading(false));
  }

  @OnAfterFetch({ target: DevicesReplaceActionAliases.FETCH_ACTION_REMOVE })
  handleAfterFetchRemove(event: FetchEvent): void {
    const validator: Validator = { name: 'required', options: {}};
    const resArray = event.data as Record<string, string>[];
    if (event && resArray.length > 0) {
      this.fieldActionRemoveAPI.addValidator(validator);
      this.fieldActionRemoveAPI.validate();
      return;
    }

    this.fieldActionRemoveAPI.removeValidator('required');
  }

  @OnAfterFetch({ target: DevicesReplaceActionAliases.FETCH_REASON_WITHDWAWN})
  handleAfterFetchReason(event: FetchEvent): void {
    const validator: Validator = { name: 'required', options: {}};
    const resArray = event.data as Record<string, string>[];
    if (event && resArray.length > 0) {
      this.fieldReasonWithdrawnAPI.addValidator(validator);
      this.fieldReasonWithdrawnAPI.validate();
      return;
    }
    this.fieldReasonWithdrawnAPI.removeValidator('required');
  }

  @OnBeforeCancel(DevicesReplaceActionAliases.FORM_DEVICES_REPLACE_ACTION_VIEWER)
  handleBeforeCancelFormDevices(event: AbortableSubmitEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }
}

export default DevicesReplaceActionController;
